import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { IVehicle } from "../../models/Vehicle";
import { ExportSource, ExportType } from "../../models/ExportOffer";
import { useMemo, useState } from "react";
import ContactFromId from "../contacts/ContactFromId";
import PriceFormat from './../../utils/priceFormat';
import useExportOffers from "../../data/useExportOffers";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import ExportOffersModal from "./ExportOffersModal";
import classNames from './../../utils/classNames';
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useNotification from "../notifications/useNotifications";


function ExportOffersTableRowInput({ label, offer, onUpdate, priceLabel }: { label: string, offer: any | null, onUpdate: (price: string) => void, priceLabel: string }) {
  return (
    <div className={classNames(offer?.isAccepted ? "bg-green-400 rounded" : "", "flex flex-col gap-1 p-1")}>
      
      {offer?.isAccepted  ? <label>{priceLabel}: £{offer?.price || ''}</label> : <>
        <label>{priceLabel}</label>
        <span className="flex items-center">
          £
          <input
            className="h-8 border p-1 ml-2 text-sm w-full"
            type="number"
            defaultValue={offer?.price || ''}
            onBlur={(e) => onUpdate(e.target.value)}
          />
        </span>
      </>}
      
    </div>
  );
}


export default function ExportOffersTableRow({ vehicle }: { vehicle: IVehicle }) {
  const { t } = useTranslation("vehicles");
  const { create, update, remove } = useExportOffers();
  const { userId, claims } = useAuth();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Export Offer"));
  const {addNotification} = useNotification();
  const [open, setOpen] = useState(false);
  const [ declineConfirmOpen, setDeclineConfirmOpen ] = useState(false);

  const isExporter = !!claims?.isExporter && !claims?.isAdmin;
  // const isExporter = true;

  const offerDefinitions = [
    { type: ExportType.Vehicle, label: t("Vehicle") },
    { type: ExportType.Engine, label: t("Engine") },
    { type: ExportType.FrontEnd, label: t("Front-End") },
  ];

  const { userOffers, highestOffers } = useMemo(() => {
    const userOffersMap: Record<string, any> = {};
    const highestMap: Record<string, any> = {};

    vehicle.exportOffers?.forEach((offer) => {
      const key = offer.type;

      // track user's own offer
      if (offer.userId === userId) {
        userOffersMap[key] = offer;
      }
      // track highest
      if (!highestMap[key] || (offer.price > highestMap[key].price && !highestMap[key].isAccepted) || (offer.isAccepted && !highestMap[key].isAccepted)) {
        highestMap[key] = offer;
      }
    });

    return { userOffers: userOffersMap, highestOffers: highestMap };
  }, [vehicle.exportOffers, userId]);

  const handleDecline = () => {

    // Find the user's offers for this vehicle; remove them
    const userOffers = vehicle.exportOffers?.filter((offer) => offer.userId === userId);
    if (userOffers) {
      userOffers.forEach((offer) => {
        remove.mutate(offer.id)
      });
    }

    // Create a new offer to decline the vehicle
    const declineOffer = {
      type: ExportType.Declined,
      source: ExportSource.Other,
      price: 0,
      userId: userId,
      isAccepted: false,
      vehicleId: vehicle.id,
    };
    create.mutate(declineOffer, {
      ...deleteHandlers,
      onSuccess: () => {
        addNotification({
          variant: "info",
          primaryText: t("Vehicle Declined"),
          secondaryText: t("You have successfully declined this vehicle."),
        })
        setDeclineConfirmOpen(false);
      },
    });

  }


  const handlePriceUpdate = (type: ExportType, price: string) => {

    // Get the existing offer for this type and user
    const offer = vehicle.exportOffers?.find((offer) => offer.type === type && offer.userId === userId);

    // If the price hasn't changed, do nothing
    if (offer && offer.price === parseFloat(price)) {
      return;
    }

    // If the price is empty and there is no previous offer, do nothing
    if (price === '' && !offer) {
      return;
    }

    // If there's an existing offer, update it
    if (offer) {
      
      offer.price = price === "" ? 0 : parseFloat(price);
      update.mutate(offer, saveHandlers);
    
    // If there's no existing offer, create a new one
    } else {

      const newOffer = {
        type: type,
        source: ExportSource.Other,
        price: price === "" ? 0 : parseFloat(price),
        userId: userId,
        isAccepted: false,
        vehicleId: vehicle.id,
      };

      create.mutate(newOffer, saveHandlers);
    }
  }


  if (isExporter) {
    // Try and get the current user's Declined offer for this vehicle
    const declinedOffer = vehicle.exportOffers?.find((offer) => offer.type === ExportType.Declined && offer.userId === userId);
    if (declinedOffer) {
      return (
        <div className="text-sm text-gray-500 flex justify-center">
          {t("You have declined this vehicle.")}
        </div>
      )
    }
    else {
      return (
      <>


        <ConfirmModal 
          open={declineConfirmOpen} 
          setOpen={setDeclineConfirmOpen} 
          title={t("Decline Vehicle")} 
          message={t("Are you sure you want to decline this vehicle? This will express your intent not to purchase parts for this vehicle and it will be hidden from you.")} 
          confirmButtonText={t("Decline")} 
          onConfirm={handleDecline} 
          isLoading={false}
        />

        <div className="w-full space-y-2" onClick={(e) => e.stopPropagation()}>
          {offerDefinitions.map(({ type, label }) => (
            <ExportOffersTableRowInput
              key={`${vehicle.id}|${type}`}
              label={label}
              priceLabel={t("{{label}} Price", { label })}
              offer={userOffers[type]}
              onUpdate={(val) => handlePriceUpdate(type, val)}
            />
          ))}

          <div className="flex justify-center">
            <ButtonInput
              label={t("Decline Vehicle")}
              onClick={() => setDeclineConfirmOpen(true)}
              classes="!bg-red-600 hover:!bg-red-700"
            />
          </div>
        </div>
      </>
      );
    }
  }

  const anyOffers = Object.values(highestOffers).length > 0;

  // Count declined offers
  const declinedOffersCount = vehicle.exportOffers?.filter(
    (offer) => offer.type === ExportType.Declined
  ).length || 0;

  // Check if all offers are declined
  const allDeclined =
    anyOffers &&
    Object.values(highestOffers).every((offer: any) => offer.type === ExportType.Declined);

  return (
    <div>
      <Modal open={open} width={"max-w-2xl"} setOpen={setOpen}>
        <ExportOffersModal vehicle={vehicle} />
        <div className="flex justify-end mt-4">
          <ButtonInput
            label={t("Close")}
            onClick={() => setOpen(false)}
            classes="!bg-red-600 hover:!bg-red-700"
          />
        </div>
      </Modal>
      {!anyOffers ? (
        <div className="text-sm text-gray-500">{t("No export offers")}</div>
      ) : (
        <div className="rounded p-1 select-none relative" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
          <div className="hover:opacity-100 opacity-0 absolute w-full h-full top-0 left-0">
            <div className="flex items-center justify-center h-full w-full bg-gray-200 rounded">
              <span className="text-sm text-gray-500">{t("Click to view offers")}</span>
            </div>
          </div>
          {allDeclined ? (
            <div>
              <div className="text-sm text-gray-500">{t("No export offers")}</div>
              {declinedOffersCount > 0 && (
                <div className="text-xs text-red-400 mt-1">
                  {declinedOffersCount === 1
                    ? t("and {{count}} has declined", { count: declinedOffersCount })
                    : t("and {{count}} have declined", { count: declinedOffersCount })}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-500 underline">{t("Best Offers")}</div>
              {offerDefinitions.map(({ type, label }) => {
                const offer = highestOffers[type];
                if (!offer || (offer.price <= 0 && !offer.isAccepted)) return null;
                return (
                  <div key={type} className={classNames(offer?.isAccepted ? "bg-green-400 rounded" : "", "mb-2")}>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">{label}</span>
                      <span className="text-sm font-medium text-gray-900">{PriceFormat(offer.price)}</span>
                    </div>
                    <div className="flex">
                      <span className="text-sm font-medium text-gray-500">{t("Offer From")}</span>
                    </div>
                    <div className="flex">
                      <span className="text-sm font-medium text-gray-900">
                        <ContactFromId contactId={offer.userId} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
